import {LitElement, PropertyValues, TemplateResult, html, nothing} from "lit";
import {customElement, property, query, queryAsync, state} from "lit/decorators.js";
import {classMap} from "lit/directives/class-map.js";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {EditorView} from "@tiptap/pm/view";
import {rteStyles} from "./RichTextEditor.styles.js";

import "../../LoadingBox.js";
import {attachTooltip} from "../../tooltipControl.js";

import {Editor, Extensions} from "@tiptap/core";
import Blockquote from "@tiptap/extension-blockquote";
import BulletList from "@tiptap/extension-bullet-list";
import CharacterCount from "@tiptap/extension-character-count";
import Document from "@tiptap/extension-document";
import History from "@tiptap/extension-history";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Text from "@tiptap/extension-text";
import Underline from "@tiptap/extension-underline";
import Utils from "../../../../main/utils/index.js";

import {CustomBold_b} from "./extensions/bold_b.js";
import {CustomBold_strong} from "./extensions/bold_strong.js";
import {CustomItalics_i} from "./extensions/italics_i.js";
import {CustomItalics_em} from "./extensions/italics_em.js";
import {SmallCaps} from "./extensions/smallCaps.js";

export const RTE_WRAPPER_CLASS = "rich-text-editor-wrapper";
export const RTE_TOOLBAR_CLASS = "rich-text-editor-toolbar";

export const RTE_WRAPPER_SELECTOR = `.${RTE_WRAPPER_CLASS}`;
export const RTE_TOOLBAR_SELECTOR = `.${RTE_TOOLBAR_CLASS}`;

const DUMMY_EDITOR_CLASS = "dummy-editor";
const DUMMY_EDITOR_SELECTOR = `.${DUMMY_EDITOR_CLASS}`;

const EDITOR_PART_NAME = "editor";
const EDITOR_ID = "editor";

// Used for styling borders
const RTE_WRAPPER_EMPTY_CLASS = "is-empty";

// Optional extensions
export const EXTENSIONS = {
	BLOCKQUOTE:  "blockquote",
	BOLD:        "bold",
	BULLETLIST:  "bulletList",
	ITALIC:      "italic",
	LINK:        "link",
	ORDEREDLIST: "orderedList",
	SMALLCAPS:   "smallCaps",
	SUBSCRIPT:   "subscript",
	SUPERSCRIPT: "superscript",
	UNDERLINE:   "underline"
};

// Things that have buttons for them in the toolbar
export const nodesAndMarks = [
	EXTENSIONS.BLOCKQUOTE,
	EXTENSIONS.BOLD,
	EXTENSIONS.BULLETLIST,
	EXTENSIONS.ITALIC,
	EXTENSIONS.LINK,
	EXTENSIONS.ORDEREDLIST,
	EXTENSIONS.SMALLCAPS,
	EXTENSIONS.SUBSCRIPT,
	EXTENSIONS.SUPERSCRIPT,
	EXTENSIONS.UNDERLINE
];

// Mapping for optional extensions
export const extensionsMap = new Map([
	[EXTENSIONS.BLOCKQUOTE, [Blockquote]],
	[EXTENSIONS.BOLD, [CustomBold_b, CustomBold_strong]],
	[EXTENSIONS.BULLETLIST, [BulletList]],
	[EXTENSIONS.ITALIC, [CustomItalics_em, CustomItalics_i]],
	[EXTENSIONS.LINK, [Link]],
	[EXTENSIONS.ORDEREDLIST, [OrderedList]],
	[EXTENSIONS.SMALLCAPS, [SmallCaps]],
	[EXTENSIONS.SUBSCRIPT, [Subscript]],
	[EXTENSIONS.SUPERSCRIPT, [Superscript]],
	[EXTENSIONS.UNDERLINE, [Underline]]
]);

// The last button in a "group" will have a class added for a visual separator
const lastButtonInGroup = [
	EXTENSIONS.UNDERLINE,
	EXTENSIONS.SMALLCAPS
];

@customElement("pn-rte")
export class RichTextEditor extends LitElement {
	@property() blurOnEnter = false;
	@property() content = ""; // TODO
	@property() editable = false;
	@property() isLoading = false;
	@property() placeholder = "";
	@property() showToolbarOnFocusOnly = false;

	// Immediately focus editor upon construction.
	@property() autofocus = false;

	// If false, the tiptap editor will not be constructed immediately,
	// and an interactable div will be the placeholder element until
	// content needs to be edited by the user. This should cut performance
	// costs if there are many editors on the page at the same time.
	@property() constructEditorImmediately = true;

	@property() disableEmojiBtn = false;

	// A list of extensions to be enabled in the editor instance. If not
	// provided, all installed extensions will be enabled.
	@property() enabledExtensions: string[] = [];

	// Maximum number of characters allowed. Default (0) is unlimited.
	@property() limit = 0;

	@property() isAnnotationNote = false;

	// See https://prosemirror.net/docs/ref/#model.ParseOptions.preserveWhitespace
	// Unsure if https://prosemirror.net/docs/ref/#model.NodeSpec.whitespace
	// would be better, but preserveWhitespace seems to work for our purposes
	@property() preserveWhitespaceFull = true;

	@property() onBlur:         ({editor, event}: {editor: Editor | null; event: FocusEvent}) => void;
	@property() onFocus:        ({editor, event}: {editor: Editor; event: FocusEvent}) => void;
	@property() onKeyDownEnter: (editor: Editor | null) => void;
	@property() onSetUpToolbar: () => void;
	@property() onUpdate:       ({editor}: {editor: Editor}) => void;

	@state() editor:               Editor | null;
	@state() enabledNodesAndMarks: string[];
	@state() isEmojiPickerSetup = false;
	@state() isEmpty:              boolean;
	@state() isLastEnterModified = false;
	@state() isSettingLink = false;
	@state() isShowingDummyEditor: boolean;

	@queryAsync(DUMMY_EDITOR_SELECTOR) _dummyEditor: HTMLElement;
	@queryAsync("emoji-picker") _emojiPicker:        HTMLElement;
	@query(".rte-wrapper") _rteWrapper:              HTMLDivElement;
	@query(RTE_TOOLBAR_SELECTOR) _toolbar:           HTMLElement;
	@query(RTE_WRAPPER_SELECTOR) _wrapper:           HTMLElement;
	@queryAsync(".ProseMirror") _proseMirrorDiv:     HTMLDivElement;

	// TODO: (await this._var) for the async ones? or make not async

	static styles = [rteStyles];

	static shadowRootOptions = {...LitElement.shadowRootOptions, delegatesFocus: true};

	buttonsMap = new Map([
		[EXTENSIONS.BOLD, {
			label:   "&#119809;",
			onClick: () => this.onClickToolbarBold(),
			tooltip: "Bold"
		}],
		[EXTENSIONS.ITALIC, {
			label:   "&#119920;",
			onClick: () => this.onClickToolbarItalic(),
			tooltip: "Italic"
		}],
		[EXTENSIONS.UNDERLINE, {
			label:   "&#9089;",
			onClick: () => this.onClickToolbarUnderline(),
			tooltip: "Underline"
		}],
		[EXTENSIONS.SUPERSCRIPT, {
			label:   "A&#7491;",
			onClick: () => this.onClickToolbarSuperscript(),
			tooltip: "Superscript"
		}],
		[EXTENSIONS.SUBSCRIPT, {
			label:   "A&#8336;",
			onClick: () => this.onClickToolbarSubscript(),
			tooltip: "Subscript"
		}],
		[EXTENSIONS.SMALLCAPS, {
			label:   "A&#7424;",
			onClick: () => this.onClickToolbarSmallCaps(),
			tooltip: "Small caps"
		}],
		[EXTENSIONS.BULLETLIST, {
			label:   "&#8226;",
			onClick: () => this.onClickToolbarBulletList(),
			tooltip: "Bullet list"
		}],
		[EXTENSIONS.ORDEREDLIST, {
			label:   "1..",
			onClick: () => this.onClickToolbarOrderedList(),
			tooltip: "Numbered list"
		}],
		[EXTENSIONS.BLOCKQUOTE, {
			label:   "&#10078;",
			onClick: () => this.onClickToolbarBlockquote(),
			tooltip: "Blockquote"
		}],
		[EXTENSIONS.LINK, {
			label:   "Link",
			onClick: () => this.onClickToolbarLink(),
			tooltip: "Link"
		}]
	]);

	willUpdate(changedProperties: PropertyValues<this>) {
		if (changedProperties.has("enabledExtensions")) {
			this.enabledNodesAndMarks = this.enabledExtensions.length > 0
				? nodesAndMarks.filter(value => this.enabledExtensions.includes(value))
				: nodesAndMarks;
		}
	}

	firstUpdated() {
		this.isShowingDummyEditor = !this.constructEditorImmediately;
		this.computeAndSetIsEmpty();

		// Set up editor
		if (this.constructEditorImmediately || this.autofocus)
			this.constructRichTextEditor();

		// Set the right classes on the toolbar for editor styles to work properly
		if (this.showToolbarOnFocusOnly || !this.editable)
			this.hideToolbar();

		// TODO: this probably isn't good enough for property updates
		attachTooltip(this._toolbar.querySelectorAll("button"), {
			tooltipPosition: "bottom",
			tooltipText:     (elem) => elem.getAttribute("data-tooltip-text")
		});
	}

	updated(changedProperties: PropertyValues<this>) {
		// Switched from showing dummy editor to real editor
		if (changedProperties.has("isShowingDummyEditor") && changedProperties.get("isShowingDummyEditor") && !this.isShowingDummyEditor) {
			this.constructRichTextEditor();
			if (this._proseMirrorDiv)
				this.focus();
		}

		if (changedProperties.has("content")) {
			if (!this.isShowingDummyEditor)
				this.setEditorContent(this.content);
			this.computeAndSetIsEmpty();
		}
	}

	computeIsEmpty() {
		return !this.content || this.content === "" || this.content === "<p></p>";
	}

	computeAndSetIsEmpty() {
		this.isEmpty = this.computeIsEmpty();
	}

	// When dummy editor is interacted with, replace it with the real one.
	replaceDummyEditor = () => {
		this.isShowingDummyEditor = false;
	};

	constructRichTextEditor = () => {
		const {autofocus, content, editable} = this;

		// Create tiptap editor
		this.editor = new Editor({
			autofocus,
			content,
			editable,
			element:     this._rteWrapper,
			editorProps: {
				attributes: {
					class: "custom-scroll-bar-light",
					id:    EDITOR_ID,
					part:  EDITOR_PART_NAME
				},
				handleKeyDown: this.onEditorKeyDown
			},
			extensions: this.getExtensionsToUse(),
			...(this.preserveWhitespaceFull && {
				parseOptions: {
					preserveWhitespace: "full"
				}
			})

		});

		// Add event handlers
		this.editor.on("blur", this.onEditorBlur.bind(this));
		this.editor.on("focus", this.onEditorFocus.bind(this));
		this.editor.on("selectionUpdate", this.onEditorSelectionUpdate.bind(this));
		this.editor.on("update", this.onEditorUpdate.bind(this));

		// Set up toolbar
		// TODO: probably needs fixing for the one place where this is used
		if (this._toolbar.children.length === 0 && this.onSetUpToolbar)
			this.onSetUpToolbar();
	};

	getExtensionsToUse = () => {
		const extensions: Extensions = [
			// Required
			Document,
			Paragraph,
			Text,
			// Other
			CharacterCount.configure({
				limit: this.limit
			}),
			History,
			ListItem,
			Placeholder.configure({
				placeholder:          this.placeholder,
				showOnlyWhenEditable: false
			})
		];

		if (this.enabledExtensions.length === 0) {
			// If unspecified, enable everything
			extensionsMap.forEach(value => {
				extensions.push(...value);
			});
		} else {
			// Otherwise, only enable what is wanted
			extensionsMap.forEach((value, key) => {
				if (this.enabledExtensions.includes(key))
					extensions.push(...value);
			});
		}

		return extensions;
	};

	// TODO: only destroy on lit component destroy?
	destroy() {
		if (this.editor) {
			this.editor.destroy();
			this.editor = null;
		}
	}

	/****************************************************************
	 ******************** Editor Event Handlers *********************
	 ****************************************************************/
	onEditorBlur = ({editor, event}: {editor: Editor | null; event: FocusEvent}) => {
		// Clicked on toolbar, or link setting prompt is open
		const relatedTarget = event.relatedTarget as HTMLElement;
		if (relatedTarget?.closest(RTE_WRAPPER_SELECTOR) || this.isSettingLink) {
			this.isSettingLink = false;
			return;
		}

		if (this.showToolbarOnFocusOnly)
			this.hideToolbar();

		if (!this.constructEditorImmediately) {
			// Destroy RTE and replace with the dummy editor
			if (editor)
				this.content = editor.getHTML();
			this.destroy();
			this.isShowingDummyEditor = true;
		}

		if (this.onBlur)
			this.onBlur({editor, event});
	};

	onEditorFocus = ({editor, event}: {editor: Editor; event: FocusEvent}) => {
		if (this.showToolbarOnFocusOnly && this.editable)
			this.showToolbar();

		if (this.onFocus)
			this.onFocus({editor, event});
	};

	onEditorKeyDown = (view: EditorView, event: KeyboardEvent) => {
		const key = event.key;

		// For some reason, the blur command causes an error in Chrome when used
		// in this function, but not in Firefox. So just.. silence it. Nothing
		// seems to break.
		const blur = () => {
			try {
				this.editor?.commands.blur();
			} catch (e) { }
		};

		if (key === "Enter") {
			if (event.altKey || event.shiftKey || event.ctrlKey || event.metaKey) {
				// This command chain causes another keydown event for "Enter",
				// so the this.isLastEnterModified flag is used to check if it
				// came from here
				this.isLastEnterModified = true;
				this.editor?.chain().enter().run();
				return true;
			} else if (this.isLastEnterModified) {
				this.isLastEnterModified = false;
				return false;
			} else {
				if (this.onKeyDownEnter)
					this.onKeyDownEnter(this.editor);

				if (this.blurOnEnter)
					blur();

				return true;
			}
		} else if (this.blurOnEnter && (key === "Escape" || key === "Esc")) {
			blur();
			return true;
		}

		return false;
	};

	onEditorSelectionUpdate = ({editor}: {editor: Editor}) => {
		this.updateEditorToolbar({editor});
	};

	onEditorUpdate = ({editor}: {editor: Editor}) => {
		this.updateEditorToolbar({editor});

		if (this.onUpdate)
			this.onUpdate({editor});
	};

	/****************************************************************
	 *********************** Editor Actions *************************
	 ****************************************************************/
	clearEditorContent = () => {
		this.content = "";
		if (this.editor)
			this.editor.commands.clearContent();
	};

	getEditorContentText() {
		if (this.editor)
			return this.editor.getText();
		else
			return this._dummyEditor.textContent;
	}

	getEditorContentHTML() {
		if (this.editor)
			return this.editor.getHTML();
		else
			return this._dummyEditor.innerHTML;
	}

	setEditorContent(content: string) {
		if (this.editor) {
			const emitOptions = false; // needed to fill the second arg

			const options: object = this.preserveWhitespaceFull
				? {preserveWhitespace: "full"}
				: {};

			this.editor.commands.setContent(content, emitOptions, options);
		}
	}

	/**
	 * Makes the editor editable or not editable, and shows/hides the toolbar
	 * accordingly. If the editor is made editable, focus will be moved inside
	 * the editor to the end of the content.
	 *
	 * @param {boolean} editable
	 */
	setEditable(editable: boolean) {
		this.editable = editable;

		if (this.editor) {
			this.editor.options.editable = editable;
			this.editor.view.update(this.editor.view.props);
		} else
			this.replaceDummyEditor();

		if (!this.editable)
			this.hideToolbar();
	}

	focus() {
		if (this.editor)
		// Always position cursor at the end of the editor
			this.editor.commands.focus("end");
		else
			this.replaceDummyEditor();
	}

	/****************************************************************
	 *********************** Toolbar Related ************************
	 ****************************************************************/
	setUpEmojiButton() {
		const emojiButton = this._toolbar.querySelector(".btn-emoji-picker");
		if (!emojiButton)
			return;

		if (this.disableEmojiBtn)
			emojiButton.classList.add("hidden");
		else if (!this.isEmojiPickerSetup) {
			// Add emoji picker to DOM here instead of in the JSX to avoid errors
			const emojiPicker = document.createElement("emoji-picker");
			emojiPicker.classList.add("hidden");

			emojiPicker.addEventListener("emoji-click", event => {
				this.editor?.chain().focus().insertContent((event as any).detail.unicode).run();
			});

			// Close emoji picker when there's a click outside of it or its button
			document.addEventListener("click", event => {
				// Get the top level emoji picker element in its shadow DOM
				const pickerRootElem = emojiPicker.shadowRoot?.querySelector(".picker");
				const target = event.composedPath()[0] as Node; // event.target won't work if it's inside the shadow dom

				// Don't close the picker if it's a click inside of it
				if (pickerRootElem?.contains(target))
					return;

				if (emojiButton && emojiPicker && !emojiPicker.isSameNode(target)
					&& !emojiButton.isSameNode(target)) {
					emojiButton.classList.remove("selected");
					emojiPicker.classList.add("hidden");
				}
			});

			// Add scroll bar styles
			const style = document.createElement("style");
			style.textContent = `
				.tabpanel {
					scrollbar-width: thin;
					scrollbar-color: #c1c1c1 #fbfbfb;
				}

				.tabpanel::-webkit-scrollbar {
					width: 10px;
					-webkit-appearance: none;
					background: #fbfbfb;
				}
				.tabpanel::-webkit-scrollbar-thumb {
					-webkit-box-shadow: inset 1px 0 0 #fbfbfb;
					-webkit-border-radius: 20px;
					background-clip: content-box;
					background-color: #c1c1c1;
				}
				.tabpanel::-webkit-scrollbar-track {
					-webkit-border-radius: 0;
					border-left: 1px solid #e8e8e8;
					background: #fbfbfb;
				}
			</style>`;

			emojiPicker.shadowRoot?.append(style);

			this._toolbar.append(emojiPicker);
			this.isEmojiPickerSetup = true;
		} else
			emojiButton.classList.remove("hidden");
	}

	updateEditorToolbar = ({editor}: {editor: Editor}) => {
		// Figure out what styles are being used in the selection and update
		// buttons highlighted accordingly
		this.enabledNodesAndMarks.forEach(nodeOrMark => {
			const button = this._toolbar.querySelector(`.editor-toolbar-btn-${nodeOrMark}`);
			if (!button)
				return;

			let isNodeOrMarkActive = false;
			const extensions = extensionsMap.get(nodeOrMark) ?? [];

			for (const extension of extensions) {
				isNodeOrMarkActive = editor.isActive(extension.name);
				if (isNodeOrMarkActive)
					break;
			}
			// Add styling if active
			if (isNodeOrMarkActive)
				button.classList.add("selected");
			else
				button.classList.remove("selected");
		});
	};

	hideToolbar = () => {
		this._wrapper.classList.add("hide-toolbar");
	};

	showToolbar = () => {
		this._wrapper.classList.remove("hide-toolbar");
	};

	/****************************************************************
	 *********************** Toolbar Buttons ************************
	 ****************************************************************/
	onClickToolbarBlockquote = () => {
		this.editor?.chain().toggleBlockquote().focus().run();
	};

	onClickToolbarBold = () => {
		if (document.getSelection()?.focusNode?.parentElement?.closest("strong"))
			this.editor?.chain().toggleBold_strong().focus().run();
		else
			this.editor?.chain().toggleBold().focus().run();
	};

	onClickToolbarBulletList = () => {
		this.editor?.chain().toggleBulletList().focus().run();
	};

	onClickToolbarItalic = () => {
		if (document.getSelection()?.focusNode?.parentElement?.closest("em"))
			this.editor?.chain().toggleItalic_em().focus().run();
		else
			this.editor?.chain().toggleItalic().focus().run();
	};

	onClickToolbarLink = () => {
		// If the text selection is a link, uses the url set for it in the prompt
		let defaultValue = this.editor?.getAttributes("link").href;
		// Otherwise, if the selected text is a valid url, it will use that
		if (!defaultValue) {
			const selectedText = window.getSelection()?.toString();
			if (selectedText && Utils.Common.isUrlValid(selectedText))
				defaultValue = selectedText;
		}

		if (!defaultValue)
			defaultValue = "https://";

		// Set this flag to prevent the editor from blurring
		this.isSettingLink = true;

		let enteredUrl = window.prompt("URL", defaultValue);

		// User clicked cancel
		if (enteredUrl === null)
			return;

		// Only accept absolute URLs
		enteredUrl = enteredUrl.trim();
		if (enteredUrl === "") {
			// User clicked enter with empty field, remove link
			this.editor?.chain().extendMarkRange("link").unsetLink().focus().run();
		} else {
			try {
				// eslint-disable-next-line no-new
				new URL(enteredUrl);
			} catch (e) {
				window.alert("Invalid URL: " + enteredUrl);
				enteredUrl = "";
			}

			this.editor?.chain().extendMarkRange("link").setLink({href: enteredUrl}).focus().run();
		}
	};

	onClickToolbarOrderedList = () => {
		this.editor?.chain().toggleOrderedList().focus().run();
	};

	onClickToolbarSmallCaps = () => {
		this.editor?.chain().toggleSmallCaps().focus().run();
	};

	onClickToolbarSubscript = () => {
		this.editor?.chain().toggleSubscript().focus().run();
	};

	onClickToolbarSuperscript = () => {
		this.editor?.chain().toggleSuperscript().focus().run();
	};

	onClickToolbarUnderline = () => {
		this.editor?.chain().toggleUnderline().focus().run();
	};

	// Open/close emoji picker
	onClickEmojiBtn = async (event: PointerEvent) => {
		const emojiButton = event.target as HTMLElement;
		if (!emojiButton)
			return;

		if (emojiButton.classList.contains("selected")) {
			emojiButton.classList.remove("selected");
			// eslint-disable-next-line @typescript-eslint/await-thenable
			(await this._emojiPicker).classList.add("hidden");
		} else {
			emojiButton.classList.add("selected");
			// eslint-disable-next-line @typescript-eslint/await-thenable
			(await this._emojiPicker).classList.remove("hidden");
		}
	};

	// TODO: cleanup
	// onKeyDown={onKeyDownShadow}

	renderEditor() {
		if (this.isShowingDummyEditor) {
			// Reset variables
			this.editor = null;
			this.isLastEnterModified = false;

			const showPlaceholder = this.placeholder !== "" && (!this.content || this.content === "" || this.content === "<p></p>");

			return html`<div
				class="${classMap({
					"custom-scroll-bar-light": true,
					[DUMMY_EDITOR_CLASS]:      true,
					disabled:                  !this.editable
				})}"
				@click="${this.editable ? this.replaceDummyEditor : nothing}"
				@focus="${this.editable ? this.replaceDummyEditor : nothing}"
				id="${EDITOR_ID}"
				part="${EDITOR_PART_NAME}"
				tabindex="${this.editable ? "0" : nothing}"
			>${showPlaceholder
				? html`<p
					class="is-editor-empty"
					data-placeholder=${this.placeholder}
				></p>`
				: unsafeHTML(this.content)
			}</div>`;
		} else
			return nothing;
	}

	renderToolbar() {
		const onButtonBlur = (event: FocusEvent) => {
			if (!this._wrapper.contains(event.relatedTarget as Node))
				this.onEditorBlur({editor: this.editor, event});
		};

		const functionButtons: TemplateResult[] = [];
		this.buttonsMap.forEach((value, key) => {
			if (!this.enabledNodesAndMarks.includes(key))
				return;

			functionButtons.push(html`<button
				aria-label="${value.tooltip}"
				@blur="${onButtonBlur}"
				class="${classMap({
					[`editor-toolbar-btn-${key}`]: true,
					"border-right":                lastButtonInGroup.includes(key)
				})}"
				@click="${value.onClick}"
				data-tooltip-text="${value.tooltip}"
			>${unsafeHTML(value.label)}</button>`);
		});

		const emojiBtn = this.disableEmojiBtn
			? nothing
			: html`<button
				aria-label="Emoji"
				@blur="${onButtonBlur}"
				class="btn-emoji-picker"
				@click="${(event: PointerEvent) => {
					if (!this.isEmojiPickerSetup)
						this.setUpEmojiButton();

					this.onClickEmojiBtn(event);
				}}"
				data-tooltip-text="Emoji"
			>=)</button>`;

		return html`${functionButtons}${emojiBtn}`;
	}

	render() {
		// TODO: lang tag is empty
		return html`
			<div
				class=${classMap({
					[RTE_WRAPPER_CLASS]:       true,
					"pn-notes-wrapper":        this.isAnnotationNote,
					editable:                  this.editable,
					[RTE_WRAPPER_EMPTY_CLASS]: this.isEmpty
				})}
				id=${`${this.id}-wrapper`}
			>
				<div class="rich-text-editor-area" lang="" part="rte-container">
					${this.isLoading
						? html`<pn-loading-box></pn-loading-box>`
						: nothing
					}
					<div
						class="${classMap({
							"rte-wrapper":         true,
							[`js-rte-${this.id}`]: true
						})}"
					>${this.renderEditor()}</div>
				</div>

				<div
					class="${`${RTE_TOOLBAR_CLASS} custom-scroll-bar-light`} "
					id="${`${this.id}-toolbar`}"
				>${this.renderToolbar()}</div>
			</div>
		`;
	}
};

declare global {
	interface HTMLElementTagNameMap {
		"pn-rte": RichTextEditor;
	}
}
